/**
 * The option panel — every tool's controls, built from that tool's schema.
 *
 * Nothing in any template hardcodes a tool's options; this is the only code
 * that creates an option control on the site, and it reads the schema the
 * feature engine published. A structural test asserts the empty panel in the
 * markup, which is what makes "no tool's options are hardcoded in a template"
 * a mechanical fact rather than a convention.
 *
 * Three decisions worth stating, because each has a rejected alternative:
 *
 *   **Children are visible and disabled, not hidden.** Hiding keep-italic
 *   until "remove formatting" is ticked makes the panel shorter and makes the
 *   feature undiscoverable — the user has no way to learn that ticking the
 *   parent is how you keep italics. The indent rail plus the dimmed state
 *   carries the dependency without hiding it.
 *
 *   **No `aria-checked="mixed"` parent.** The tri-state parent checkbox is the
 *   pattern that validates and does not work: `indeterminate` is a DOM
 *   property with no HTML attribute, it is announced inconsistently across
 *   NVDA/JAWS/VoiceOver, and it invites the reading "some children are on"
 *   when the actual meaning here is "this stage runs". The parent is a plain
 *   on/off and the children are a `role="group"` labelled by it.
 *
 *   **Exclusive accordion on mobile only.** The cleaner has roughly fifteen
 *   options; rendered flat on a phone it is a wall. Above 700px there is room
 *   for several groups at once and forcing one open is just extra clicks. The
 *   native `name` attribute on `<details>` does this where it exists, with a
 *   JS fallback where it does not.
 *
 *   **The group the schema opens is not in the accordion.** See
 *   `accordionMembers`: it is the tool's main input, and the accordion only
 *   ever takes turns among the optional groups below it. Nor are two groups
 *   an `enabledBy` link joins, because a field and the key that enables it
 *   are set together.
 */

import { el, qsa } from './dom.ts';
import { t } from './i18n.ts';
import {
  type Choice,
  childrenEnabled,
  defaultValues,
  enabledByOthers,
  type OptionField,
  type OptionsSchema,
  type OptionValue,
  type OptionValues,
  validateSchema,
  walkFields,
} from './options-schema.ts';

export interface OptionPanelHandle {
  values: () => OptionValues;
  setValues: (next: OptionValues) => void;
  destroy: () => void;
}

const MOBILE = '(max-width: 699px)';
const supportsExclusiveDetails = (): boolean =>
  typeof HTMLDetailsElement !== 'undefined' && 'name' in HTMLDetailsElement.prototype;

let uid = 0;
function nextId(key: string): string {
  uid += 1;
  return `opt-${key}-${uid}`;
}

/**
 * `<input type="color">` speaks '#rrggbb' and the model speaks channels. Both
 * conversions live here so the hex never travels further than this file.
 */
export function fromHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function toHex(c: { r: number; g: number; b: number }): string {
  const two = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${two(c.r)}${two(c.g)}${two(c.b)}`;
}

/** `hh:mm:ss.mmm` or a bare millisecond count, both accepted, ms out. */
export function parseDuration(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  if (/^[+-]?\d+$/.test(text)) return Number(text);
  const match = /^([+-])?(?:(\d+):)?(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?$/.exec(text);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const h = Number(match[2] ?? 0);
  const m = Number(match[3] ?? 0);
  const s = Number(match[4] ?? 0);
  const frac = (match[5] ?? '').padEnd(3, '0');
  return sign * (h * 3_600_000 + m * 60_000 + s * 1_000 + Number(frac));
}

/** A rational framerate, or null. 24000/1001 is not 23.976 and is not rounded to it. */
export function parseFramerate(raw: string): { num: number; den: number } | null {
  const text = raw.trim();
  if (text === '') return null;
  const ratio = /^(\d+)\s*\/\s*(\d+)$/.exec(text);
  if (ratio) {
    const num = Number(ratio[1]);
    const den = Number(ratio[2]);
    return den > 0 ? { num, den } : null;
  }
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const decimals = (text.split('.')[1] ?? '').length;
  const den = 10 ** decimals;
  return { num: Math.round(Number(text) * den), den };
}

/**
 * A framerate value as the box shows it, in a form `parseFramerate` reads back
 * to the same rate: `25`, or `24000/1001` rather than a rounded `23.976`.
 */
export function framerateText(value: OptionValue | undefined): string {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return '';
  const { num, den } = value as { num?: unknown; den?: unknown };
  if (typeof num !== 'number' || typeof den !== 'number' || den <= 0) return '';
  return den === 1 ? String(num) : `${num}/${den}`;
}

/**
 * Groups (by index) that `enabledBy` joins across a group boundary: the group
 * holding a field another group's key enables, and the group holding that key.
 *
 * Only a link that CROSSES groups counts. A field enabled by a key beside it
 * is already on screen with its enabler, so taking its group out of the
 * accordion would shrink the accordion and fix nothing.
 */
function linkedGroups(schema: OptionsSchema): ReadonlySet<number> {
  // Nested keys too: an enabler may be a child, and a child lives in the
  // group its top-level parent is drawn in.
  const groupOf = new Map<string, number>();
  schema.groups.forEach((group, index) => {
    for (const field of walkFields({ version: 1, groups: [group] })) groupOf.set(field.key, index);
  });
  const linked = new Set<number>();
  schema.groups.forEach((group, index) => {
    for (const field of walkFields({ version: 1, groups: [group] })) {
      for (const key of field.enabledBy ?? []) {
        const enabler = groupOf.get(key);
        if (enabler === undefined || enabler === index) continue;
        linked.add(index);
        linked.add(enabler);
      }
    }
  });
  return linked;
}

/**
 * Which groups (by index) take turns in the mobile accordion. Empty when
 * fewer than two would: a `name` shared by one group does nothing.
 *
 * A GROUP THE SCHEMA OPENS (`open: true`) STAYS OUT. The publisher opens the
 * group that holds the tool's main input, and the accordion must never close
 * it because the user opened a second group to finish the job. That is what
 * happened on /sync-subtitles at 6772448: "First line" and "Last line" shared
 * one `name`, so at phone width opening the second point closed the first,
 * and Tab out of the first point's fields reached a collapsed second one. The
 * two points are one input, filled together.
 *
 * SO DOES EVERY GROUP AN `enabledBy` LINK JOINS TO ANOTHER (`linkedGroups`).
 * On /fix-subtitle-timing "When two cues overlap" (Overlapping cues) is
 * enabled by "Minimum gap between cues" (Gaps between cues), because the
 * strategy also decides which cue gives way to make the gap. Under one `name`
 * the user could type the gap or watch the strategy come alive, never both:
 * opening either group closed the other. The rule reads the link from the
 * schema rather than naming that page, so the next tool to link two groups
 * gets the same treatment without anyone remembering this one.
 *
 * Both ends sit out, not a pair under a second `name`: a shared `name` is
 * exactly what makes two groups close each other, so there is no name a
 * linked pair could share with each other and still be open together. They
 * still start closed on a phone; only the user opens them.
 *
 * Every other group the schema leaves closed is still exclusive, so the
 * cleaner, which opens none of its nine and links none, keeps the accordion
 * the brief asks for.
 */
export function accordionMembers(schema: OptionsSchema): readonly number[] {
  const linked = linkedGroups(schema);
  const members = schema.groups.flatMap((group, index) =>
    group.open === true || linked.has(index) ? [] : [index],
  );
  return members.length >= 2 ? members : [];
}

function choiceControls(
  field: OptionField & { choices: readonly Choice[] },
  kind: 'select' | 'radio',
  value: OptionValue,
  onInput: (v: OptionValue) => void,
): HTMLElement {
  if (kind === 'select') {
    const select = el('select', { class: 'select', id: nextId(field.key) });
    for (const choice of field.choices) {
      const option = el('option', { value: choice.value }, [choice.label]);
      if (choice.value === value) option.selected = true;
      select.append(option);
    }
    select.addEventListener('change', () => onInput(select.value));
    return select;
  }
  const group = el('div', { class: 'opt__row', role: 'radiogroup', 'aria-label': field.label });
  const name = nextId(field.key);
  for (const choice of field.choices) {
    const id = nextId(`${field.key}-${choice.value}`);
    const input = el('input', { type: 'radio', name, id, value: choice.value });
    if (choice.value === value) input.checked = true;
    input.addEventListener('change', () => {
      if (input.checked) onInput(choice.value);
    });
    const label = el('label', { class: 'opt__label', for: id }, [choice.label]);
    group.append(el('span', { class: 'opt opt--check' }, [input, label]));
  }
  return group;
}

export function mountOptionPanel(
  host: HTMLElement,
  schema: OptionsSchema,
  onChange: (values: OptionValues) => void,
): OptionPanelHandle {
  const problems = validateSchema(schema);
  if (problems.length > 0) {
    // Loud and structured. A schema this renderer cannot draw is a
    // conversation with the feature that published it, not something to
    // approximate.
    console.error('[options] unrenderable schema:', problems);
    throw new Error(`options schema is not renderable: ${problems.join('; ')}`);
  }

  const values: OptionValues = defaultValues(schema);
  const setters: (() => void)[] = [];
  /**
   * Controls that can SHOW a value set from outside, keyed by field. `setValues`
   * used to update the stored values and nothing on screen, so a framerate the
   * tool page pre-filled from the file was sent to the op while the box stayed
   * empty, and the next keystroke in it overwrote the value unseen.
   */
  const shows = new Map<string, (value: OptionValue) => void>();

  const emit = () => onChange({ ...values });

  function set(key: string, value: OptionValue): void {
    values[key] = value;
    for (const refresh of setters) refresh();
    emit();
  }

  function renderField(field: OptionField, depth: number): HTMLElement {
    const wrapper = el('div', { class: 'opt', 'data-field': field.key, 'data-type': field.type });
    const id = nextId(field.key);
    let control: HTMLElement;

    switch (field.type) {
      case 'boolean': {
        wrapper.classList.add('opt--check');
        const input = el('input', { type: 'checkbox', id });
        input.checked = values[field.key] === true;
        input.addEventListener('change', () => set(field.key, input.checked));
        // Shown from outside too: picking a reading-speed standard sets
        // "Count spaces" to the standard's own rule, and a box left showing
        // the old tick would contradict the value being sent.
        shows.set(field.key, (value) => {
          input.checked = value === true;
        });
        wrapper.append(input, el('label', { class: 'opt__label', for: id }, [field.label]));
        control = input;
        break;
      }
      case 'number': {
        const input = el('input', {
          class: 'input',
          type: 'number',
          id,
          inputmode: 'decimal',
          ...(field.min !== undefined ? { min: field.min } : {}),
          ...(field.max !== undefined ? { max: field.max } : {}),
          ...(field.step !== undefined ? { step: field.step } : {}),
        });
        const initial = values[field.key];
        input.value = typeof initial === 'number' ? String(initial) : '';
        input.addEventListener('input', () =>
          set(field.key, input.value === '' ? null : Number(input.value)),
        );
        // The reading-speed limit follows the standard picked above it; the
        // box must show the number that is actually applied.
        shows.set(field.key, (value) => {
          input.value = typeof value === 'number' ? String(value) : '';
        });
        wrapper.append(el('label', { class: 'opt__label', for: id }, [field.label]));
        const row = el('div', { class: 'opt__row' }, [input]);
        if (field.unit) row.append(el('span', { class: 'u-caption u-muted' }, [field.unit]));
        wrapper.append(row);
        control = input;
        break;
      }
      case 'text': {
        const input = el('input', {
          class: 'input',
          type: 'text',
          id,
          ...(field.placeholder ? { placeholder: field.placeholder } : {}),
        });
        const initial = values[field.key];
        input.value = typeof initial === 'string' ? initial : '';
        input.addEventListener('input', () => set(field.key, input.value));
        wrapper.append(el('label', { class: 'opt__label', for: id }, [field.label]), input);
        control = input;
        break;
      }
      case 'duration': {
        const input = el('input', {
          class: 'input',
          type: 'text',
          id,
          inputmode: 'text',
          // B3: "shift accepting ms *or* hh:mm:ss.mmm".
          placeholder: '2500 or 00:00:02.500',
        });
        const initial = values[field.key];
        input.value = typeof initial === 'number' ? String(initial) : '';
        input.addEventListener('input', () => {
          const ms = parseDuration(input.value);
          input.setAttribute('aria-invalid', ms === null && input.value !== '' ? 'true' : 'false');
          if (ms !== null || input.value === '') set(field.key, ms);
        });
        // Shown from outside too: /auto-sync-subtitles fills the shift in from
        // the matched audio, and a box still reading 0 would hide what is sent.
        shows.set(field.key, (value) => {
          input.value = typeof value === 'number' ? String(value) : '';
          input.setAttribute('aria-invalid', 'false');
        });
        wrapper.append(el('label', { class: 'opt__label', for: id }, [field.label]), input);
        control = input;
        break;
      }
      case 'framerate': {
        const input = el('input', {
          class: 'input',
          type: 'text',
          id,
          inputmode: 'decimal',
          placeholder: '25 or 24000/1001',
        });
        input.addEventListener('input', () => {
          const fps = parseFramerate(input.value);
          input.setAttribute('aria-invalid', fps === null && input.value !== '' ? 'true' : 'false');
          set(field.key, fps === null ? null : ({ ...fps } as OptionValue));
        });
        const show = (value: OptionValue) => {
          input.value = framerateText(value);
          input.setAttribute('aria-invalid', 'false');
        };
        show(values[field.key] ?? null);
        shows.set(field.key, show);
        wrapper.append(el('label', { class: 'opt__label', for: id }, [field.label]));
        const row = el('div', { class: 'opt__row' }, [input]);
        if (field.choices) {
          for (const choice of field.choices) {
            const button = el('button', { type: 'button', class: 'chip' }, [choice.label]);
            button.addEventListener('click', () => {
              input.value = choice.value;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            });
            row.append(button);
          }
        }
        wrapper.append(row);
        control = input;
        break;
      }
      case 'grid9': {
        // Nine cells plus a clear. Radios, not buttons: this is one choice from
        // a fixed set, which is exactly what a radiogroup is, and it gives
        // arrow-key navigation and a screen-reader position announcement for
        // free. Alignment 1 is BOTTOM-left and 7 is top-left (ASS numbering),
        // so the grid is laid out from 7 down to 1 to match what the eye sees.
        wrapper.append(el('span', { class: 'opt__label' }, [field.label]));
        const grid = el('div', {
          class: 'grid9',
          role: 'radiogroup',
          'aria-label': field.label,
        });
        const name = nextId(field.key);
        const ROWS: readonly (readonly number[])[] = [
          [7, 8, 9],
          [4, 5, 6],
          [1, 2, 3],
        ];
        const CELL_LABEL: Readonly<Record<number, string>> = {
          7: 'Top left',
          8: 'Top centre',
          9: 'Top right',
          4: 'Middle left',
          5: 'Middle centre',
          6: 'Middle right',
          1: 'Bottom left',
          2: 'Bottom centre',
          3: 'Bottom right',
        };
        for (const row of ROWS) {
          for (const cell of row) {
            const id = nextId(`${field.key}-${cell}`);
            const input = el('input', {
              type: 'radio',
              name,
              id,
              class: 'u-visually-hidden',
              value: String(cell),
            });
            input.checked = values[field.key] === cell;
            input.addEventListener('change', () => {
              if (input.checked) set(field.key, cell);
            });
            const label = el('label', { class: 'grid9__cell', for: id }, [
              el('span', { class: 'u-visually-hidden' }, [t(CELL_LABEL[cell] ?? String(cell))]),
            ]);
            grid.append(input, label);
          }
        }
        wrapper.append(grid);
        // The clear. `null` means "inherit from the style" and is a different
        // thing from any of the nine — without it the state is unreachable
        // once a cell has been picked.
        const clearId = nextId(`${field.key}-clear`);
        const clear = el('input', { type: 'radio', name, id: clearId, value: '' });
        clear.checked = values[field.key] === null;
        clear.addEventListener('change', () => {
          if (clear.checked) set(field.key, null);
        });
        wrapper.append(
          el('span', { class: 'opt opt--check' }, [
            clear,
            el('label', { class: 'opt__label', for: clearId }, [t('Leave as the style sets it')]),
          ]),
        );
        control = grid;
        break;
      }
      case 'color': {
        // The value is {r,g,b,a}, never a hex string — a hex string makes
        // equality case-sensitive and silently breaks dedupe and style
        // coalescing downstream. `<input type="color">` speaks hex, so the hex
        // stops here: it is parsed into channels on the way in and formatted
        // back on the way out, and no hex ever reaches the op.
        wrapper.append(el('label', { class: 'opt__label', for: id }, [field.label]));
        const current = (values[field.key] ?? null) as {
          r: number;
          g: number;
          b: number;
          a: number;
        } | null;
        const swatch = el('input', { class: 'opt__color', type: 'color', id });
        swatch.value = current ? toHex(current) : '#ffffff';
        const row = el('div', { class: 'opt__row' }, [swatch]);

        const emit = (hex: string, alpha: number) => {
          const rgb = fromHex(hex);
          if (rgb) set(field.key, { ...rgb, a: alpha } as OptionValue);
        };
        let alpha = current?.a ?? 1;
        swatch.addEventListener('input', () => emit(swatch.value, alpha));

        if (field.alpha) {
          const alphaId = nextId(`${field.key}-a`);
          const slider = el('input', {
            type: 'range',
            id: alphaId,
            min: 0,
            max: 100,
            step: 1,
            class: 'opt__alpha',
          });
          slider.value = String(Math.round(alpha * 100));
          slider.addEventListener('input', () => {
            alpha = Number(slider.value) / 100;
            emit(swatch.value, alpha);
          });
          row.append(
            el('label', { class: 'u-visually-hidden', for: alphaId }, [t('Opacity')]),
            slider,
          );
        }
        wrapper.append(row);
        control = swatch;
        break;
      }
      case 'select':
      case 'radio': {
        wrapper.append(el('span', { class: 'opt__label' }, [field.label]));
        control = choiceControls(field, field.type, values[field.key] ?? null, (v) =>
          set(field.key, v),
        );
        wrapper.append(control);
        break;
      }
      case 'repeat': {
        wrapper.append(el('span', { class: 'opt__label' }, [field.label]));
        const list = el('div', {
          class: 'opt__children',
          role: 'group',
          'aria-label': field.label,
        });
        const rows = (values[field.key] as Record<string, OptionValue>[] | undefined) ?? [];
        const redraw = () => {
          list.replaceChildren();
          rows.forEach((row, index) => {
            const rowEl = el('div', {
              class: 'opt__row',
              role: 'group',
              'aria-label': `${field.rowLabel} ${index + 1}`,
            });
            for (const sub of field.fields) {
              const subValues: OptionValues = row;
              const subInput = el('input', {
                class: 'input',
                type: 'text',
                'aria-label': `${sub.label} (${field.rowLabel} ${index + 1})`,
                placeholder: sub.label,
              });
              const existing = subValues[sub.key];
              subInput.value = existing === null || existing === undefined ? '' : String(existing);
              subInput.addEventListener('input', () => {
                row[sub.key] =
                  sub.type === 'duration' ? parseDuration(subInput.value) : subInput.value;
                emit();
              });
              // Each box carries its own visible name. The placeholder named
              // it, but a row starts filled ("0 0 0" on /shift-subtitles) and a
              // filled box shows no placeholder; a heading row above did not
              // survive a phone, where the boxes stack one per line.
              rowEl.append(
                el('label', { class: 'opt__cell' }, [
                  el('span', { class: 'opt__col-label', 'aria-hidden': 'true' }, [sub.label]),
                  subInput,
                ]),
              );
            }
            const remove = el('button', { type: 'button', class: 'btn btn--ghost' }, ['Remove']);
            remove.addEventListener('click', () => {
              rows.splice(index, 1);
              redraw();
              emit();
            });
            rowEl.append(remove);
            list.append(rowEl);
          });
          const max = field.max ?? null;
          if (max === null || rows.length < max) {
            const add = el('button', { type: 'button', class: 'btn btn--secondary' }, [
              field.addLabel,
            ]);
            add.addEventListener('click', () => {
              rows.push({});
              redraw();
              emit();
            });
            list.append(add);
          }
        };
        values[field.key] = rows as OptionValue;
        redraw();
        wrapper.append(list);
        control = list;
        break;
      }
      default: {
        // An unknown field type is a schema this build does not understand.
        // Rendering nothing silently would hide a whole tool's controls.
        const unknown = field as { key: string; type: string };
        console.error(`[options] unknown field type "${unknown.type}" for "${unknown.key}"`);
        control = el('p', { class: 'u-caption u-muted' }, ['—']);
        wrapper.append(control);
      }
    }

    if (field.help) wrapper.append(el('p', { class: 'opt__help' }, [field.help]));

    if (field.children && field.children.length > 0) {
      const kids = el('div', {
        class: 'opt__children',
        role: 'group',
        'aria-label': field.label,
      });
      for (const child of field.children) kids.append(renderField(child, depth + 1));
      wrapper.append(kids);

      const refresh = () => {
        const enabled = childrenEnabled(field, values[field.key] ?? null);
        kids.classList.toggle('opt--disabled', !enabled);
        for (const input of qsa<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
          'input, select, button',
          kids,
        )) {
          input.disabled = !enabled;
        }
      };
      setters.push(refresh);
      refresh();
    }

    // A field other fields enable (`enabledBy`). Separate from the children
    // refresh above because `validateSchema` allows it only on a top-level
    // field, so no parent's refresh ever touches these inputs too.
    if (field.enabledBy && field.enabledBy.length > 0) {
      const refreshOwn = () => {
        const enabled = enabledByOthers(field, schema, values);
        wrapper.classList.toggle('opt--disabled', !enabled);
        for (const input of qsa<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
          'input, select, button',
          wrapper,
        )) {
          input.disabled = !enabled;
        }
      };
      setters.push(refreshOwn);
      refreshOwn();
    }

    return wrapper;
  }

  const groups: HTMLDetailsElement[] = [];
  host.replaceChildren();
  schema.groups.forEach((group, index) => {
    const details = el('details', { class: 'optgroup', 'data-group': group.key });
    if (group.open ?? index === 0) details.open = true;
    const summary = el('summary', {}, [group.label]);
    const body = el('div', { class: 'optgroup__body' });
    if (group.help) body.append(el('p', { class: 'opt__help' }, [group.help]));
    for (const field of group.fields) body.append(renderField(field, 0));
    details.append(summary, body);
    host.append(details);
    groups.push(details as HTMLDetailsElement);
  });

  /* ── exclusive accordion, mobile only ──────────────────────────────── */
  const media = typeof matchMedia === 'function' ? matchMedia(MOBILE) : null;
  uid += 1;
  const accordionName = `optgroup-${uid}`;
  const native = supportsExclusiveDetails();
  // Only these take turns; a group outside them is never closed for another.
  const members = accordionMembers(schema).flatMap((index) => groups[index] ?? []);

  const onToggle = (ev: Event) => {
    if (!media?.matches || native) return;
    const opened = ev.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
    if (!members.includes(opened)) return;
    for (const other of members) if (other !== opened) other.open = false;
  };

  /**
   * Two bugs live here and both were found in a browser rather than in a test,
   * because both are about what happens when the viewport CHANGES:
   *
   *   1. Relying on the media query's `change` event alone left the `name`
   *      attribute set at desktop width — measured at 1000px with
   *      `matchMedia('(max-width: 699px)').matches === false` and the attribute
   *      still on both groups, so a desktop user could only ever see one option
   *      group at a time. That is the wall the accordion exists to prevent,
   *      moved to the screen with room for it. `resize` is a second, cheaper
   *      signal that cannot be missed.
   *
   *   2. Re-applying unconditionally would collapse the user's open group on
   *      every call — and on a phone, scrolling resizes the viewport as the URL
   *      bar hides. So the collapse happens only on the TRANSITION into mobile,
   *      never on a re-apply while already there.
   */
  let lastMobile: boolean | null = null;

  const applyAccordion = () => {
    const mobile = media?.matches ?? false;
    if (native) {
      for (const details of members) {
        if (mobile) details.setAttribute('name', accordionName);
        else details.removeAttribute('name');
      }
    }
    if (mobile && lastMobile !== true) {
      // Entering mobile: back to the first-render state, the first group (or
      // the one the schema opens) and nothing else, or the phone opens on a
      // wall of options.
      groups.forEach((details, index) => {
        details.open = schema.groups[index]?.open ?? index === 0;
      });
    }
    lastMobile = mobile;
  };

  for (const details of groups) details.addEventListener('toggle', onToggle);
  media?.addEventListener('change', applyAccordion);
  addEventListener('resize', applyAccordion, { passive: true });
  applyAccordion();

  emit();

  return {
    values: () => ({ ...values }),
    setValues(next) {
      Object.assign(values, next);
      for (const [key, value] of Object.entries(next)) shows.get(key)?.(value);
      for (const refresh of setters) refresh();
    },
    destroy() {
      media?.removeEventListener('change', applyAccordion);
      removeEventListener('resize', applyAccordion);
      for (const details of groups) details.removeEventListener('toggle', onToggle);
      host.replaceChildren();
    },
  };
}
